import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  HelpCircle,
  Award,
  BarChart3,
} from "lucide-react";

// Chart Quiz Data - Real trading scenarios
const CHART_QUIZZES = [
  {
    id: 1,
    title: "RSI Oversold",
    description: "RSI has dropped to 25 and is starting to curve upward. Price is at lower Bollinger Band.",
    chartPattern: "oversold_reversal",
    indicators: {
      rsi: 25,
      macd: "Bullish crossover forming",
      bb_position: "At lower band",
      trend: "Downtrend slowing"
    },
    correctAnswer: "BUY",
    explanation: "RSI below 30 indicates oversold conditions. Combined with price at lower Bollinger Band and MACD bullish crossover, this is a strong BUY signal. The downtrend is losing momentum.",
    difficulty: "beginner"
  },
  {
    id: 2,
    title: "RSI Overbought",
    description: "RSI has reached 78. Price is touching upper Bollinger Band. MACD histogram is shrinking.",
    chartPattern: "overbought_reversal",
    indicators: {
      rsi: 78,
      macd: "Histogram shrinking",
      bb_position: "At upper band",
      trend: "Uptrend weakening"
    },
    correctAnswer: "SELL",
    explanation: "RSI above 70 indicates overbought conditions. Price at upper Bollinger Band with shrinking MACD histogram suggests the uptrend is exhausting. Time to SELL or take profits.",
    difficulty: "beginner"
  },
  {
    id: 3,
    title: "MACD Crossover",
    description: "MACD line just crossed above the signal line. RSI is at 45 (neutral). Price broke above SMA20.",
    chartPattern: "macd_bullish",
    indicators: {
      rsi: 45,
      macd: "Bullish crossover confirmed",
      bb_position: "Middle of bands",
      trend: "New uptrend starting"
    },
    correctAnswer: "BUY",
    explanation: "MACD bullish crossover is a strong buy signal, especially when RSI is neutral (not overbought). Price breaking above SMA20 confirms the new uptrend.",
    difficulty: "beginner"
  },
  {
    id: 4,
    title: "Death Cross",
    description: "EMA12 just crossed below EMA26. RSI is at 42. Price is below all moving averages.",
    chartPattern: "death_cross",
    indicators: {
      rsi: 42,
      macd: "Bearish crossover",
      bb_position: "Below middle band",
      trend: "Downtrend confirmed"
    },
    correctAnswer: "SELL",
    explanation: "EMA death cross (12 crossing below 26) is a bearish signal. With price below all MAs and MACD bearish, this confirms a downtrend. SELL or avoid buying.",
    difficulty: "intermediate"
  },
  {
    id: 5,
    title: "Bollinger Squeeze",
    description: "Bollinger Bands are very narrow (squeeze). RSI is at 50. Price just broke above upper band with high volume.",
    chartPattern: "bb_squeeze_breakout",
    indicators: {
      rsi: 50,
      macd: "Turning positive",
      bb_position: "Breaking upper band",
      trend: "Breakout starting"
    },
    correctAnswer: "BUY",
    explanation: "Bollinger squeeze followed by breakout above upper band indicates a strong move is starting. This is a BUY signal. The squeeze shows consolidation, and breakout shows direction.",
    difficulty: "intermediate"
  },
  {
    id: 6,
    title: "Divergence Warning",
    description: "Price made a new high, but RSI made a lower high. MACD histogram is declining while price rises.",
    chartPattern: "bearish_divergence",
    indicators: {
      rsi: 65,
      macd: "Bearish divergence",
      bb_position: "Near upper band",
      trend: "Weakening momentum"
    },
    correctAnswer: "SELL",
    explanation: "Bearish divergence (price up, RSI down) is a warning sign. This often precedes a reversal. Smart traders SELL or tighten stops when they see divergence.",
    difficulty: "advanced"
  },
  {
    id: 7,
    title: "Support Bounce",
    description: "Price touched a key support level for the 3rd time and bounced. RSI is at 35. Stochastic showing bullish crossover.",
    chartPattern: "support_bounce",
    indicators: {
      rsi: 35,
      macd: "Neutral",
      stochastic: "Bullish crossover at 20",
      trend: "Testing support"
    },
    correctAnswer: "BUY",
    explanation: "Triple bottom at support is a strong reversal pattern. RSI near oversold with Stochastic bullish crossover confirms buying pressure. BUY with stop below support.",
    difficulty: "intermediate"
  },
  {
    id: 8,
    title: "Resistance Rejection",
    description: "Price hit resistance and formed a shooting star candle. RSI is at 72. Volume was high on rejection.",
    chartPattern: "resistance_rejection",
    indicators: {
      rsi: 72,
      macd: "Momentum fading",
      volume: "High on rejection",
      trend: "Rejection at resistance"
    },
    correctAnswer: "SELL",
    explanation: "Shooting star at resistance with overbought RSI is a classic SELL signal. High volume on rejection confirms sellers are stepping in. SELL or wait for better entry.",
    difficulty: "intermediate"
  },
  {
    id: 9,
    title: "Trend Continuation",
    description: "Strong uptrend. Price pulled back to SMA20 and bounced. RSI dropped from 70 to 55 and turning up.",
    chartPattern: "pullback_buy",
    indicators: {
      rsi: 55,
      macd: "Still positive",
      bb_position: "At middle band",
      trend: "Uptrend pullback"
    },
    correctAnswer: "BUY",
    explanation: "Buying pullbacks in an uptrend is a high-probability strategy. RSI cooled off but not oversold, MACD still positive. This is an ideal BUY entry in the trend.",
    difficulty: "intermediate"
  },
  {
    id: 10,
    title: "Choppy Market",
    description: "Price moving sideways for days. RSI oscillating between 45-55. MACD flat near zero. ADX at 15.",
    chartPattern: "sideways_chop",
    indicators: {
      rsi: 50,
      macd: "Flat near zero",
      adx: 15,
      trend: "No trend (sideways)"
    },
    correctAnswer: "HOLD",
    explanation: "ADX below 20 means no trend. RSI and MACD neutral confirm choppy conditions. Best action is HOLD/WAIT. Trading choppy markets leads to losses from whipsaws.",
    difficulty: "beginner"
  },
  {
    id: 11,
    title: "False Breakout",
    description: "Price broke above resistance but immediately reversed back below. RSI showed divergence before breakout.",
    chartPattern: "false_breakout",
    indicators: {
      rsi: 60,
      macd: "Weakening",
      volume: "Low on breakout",
      trend: "Failed breakout"
    },
    correctAnswer: "SELL",
    explanation: "False breakouts (fakeouts) often trap buyers. Low volume on breakout and RSI divergence were warning signs. After a false breakout, SELL as price usually drops sharply.",
    difficulty: "advanced"
  },
  {
    id: 12,
    title: "Golden Cross",
    description: "SMA50 just crossed above SMA200. RSI is at 58. Price is above both MAs with increasing volume.",
    chartPattern: "golden_cross",
    indicators: {
      rsi: 58,
      macd: "Positive and rising",
      volume: "Increasing",
      trend: "Major uptrend starting"
    },
    correctAnswer: "BUY",
    explanation: "Golden Cross (SMA50 above SMA200) is one of the most reliable bullish signals. Combined with healthy RSI and rising volume, this signals a major uptrend. Strong BUY.",
    difficulty: "beginner"
  },
];

// Visual Chart Component (simplified visual representation)
const ChartVisual = ({ pattern, indicators }) => {
  const getPatternVisual = () => {
    switch (pattern) {
      case "oversold_reversal":
        return (
          <div className="h-32 flex items-end justify-center gap-1">
            {[70, 60, 45, 35, 25, 20, 18, 22, 28].map((h, i) => (
              <div key={i} className={`w-4 rounded-t ${i >= 6 ? 'bg-green-500' : 'bg-red-500'}`} style={{height: `${h}%`}} />
            ))}
          </div>
        );
      case "overbought_reversal":
        return (
          <div className="h-32 flex items-end justify-center gap-1">
            {[30, 45, 55, 65, 75, 82, 80, 76, 72].map((h, i) => (
              <div key={i} className={`w-4 rounded-t ${i >= 6 ? 'bg-red-500' : 'bg-green-500'}`} style={{height: `${h}%`}} />
            ))}
          </div>
        );
      case "macd_bullish":
      case "golden_cross":
        return (
          <div className="h-32 flex items-end justify-center gap-1">
            {[40, 38, 42, 45, 50, 55, 62, 68, 75].map((h, i) => (
              <div key={i} className="w-4 bg-green-500 rounded-t" style={{height: `${h}%`}} />
            ))}
          </div>
        );
      case "death_cross":
      case "bearish_divergence":
        return (
          <div className="h-32 flex items-end justify-center gap-1">
            {[75, 72, 68, 65, 58, 52, 45, 40, 35].map((h, i) => (
              <div key={i} className="w-4 bg-red-500 rounded-t" style={{height: `${h}%`}} />
            ))}
          </div>
        );
      case "sideways_chop":
        return (
          <div className="h-32 flex items-end justify-center gap-1">
            {[50, 52, 48, 51, 49, 53, 47, 52, 50].map((h, i) => (
              <div key={i} className={`w-4 rounded-t ${h > 50 ? 'bg-green-500' : 'bg-red-500'}`} style={{height: `${h}%`}} />
            ))}
          </div>
        );
      default:
        return (
          <div className="h-32 flex items-end justify-center gap-1">
            {[40, 45, 50, 48, 55, 60, 58, 65, 70].map((h, i) => (
              <div key={i} className="w-4 bg-blue-500 rounded-t" style={{height: `${h}%`}} />
            ))}
          </div>
        );
    }
  };

  return (
    <div className="bg-zinc-800 rounded-lg p-4">
      <div className="mb-3">
        {getPatternVisual()}
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        {Object.entries(indicators).map(([key, value]) => (
          <div key={key} className="bg-zinc-700 p-2 rounded">
            <span className="text-zinc-400 capitalize">{key.replace('_', ' ')}: </span>
            <span className="text-white">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export function ChartQuiz() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set());

  const currentQuiz = CHART_QUIZZES[currentIndex];
  const progress = (answeredQuestions.size / CHART_QUIZZES.length) * 100;

  const handleAnswer = (answer) => {
    if (showResult) return;
    
    setSelectedAnswer(answer);
    setShowResult(true);
    
    const isCorrect = answer === currentQuiz.correctAnswer;
    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));
    setAnsweredQuestions(prev => new Set([...prev, currentQuiz.id]));
  };

  const handleNext = () => {
    setSelectedAnswer(null);
    setShowResult(false);
    setCurrentIndex((prev) => (prev + 1) % CHART_QUIZZES.length);
  };

  const handlePrev = () => {
    setSelectedAnswer(null);
    setShowResult(false);
    setCurrentIndex((prev) => (prev - 1 + CHART_QUIZZES.length) % CHART_QUIZZES.length);
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore({ correct: 0, total: 0 });
    setAnsweredQuestions(new Set());
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "beginner": return "bg-green-600";
      case "intermediate": return "bg-yellow-600";
      case "advanced": return "bg-red-600";
      default: return "bg-blue-600";
    }
  };

  const getButtonStyle = (answer) => {
    if (!showResult) {
      if (answer === "BUY") return "border-green-500 hover:bg-green-500/20 text-green-400";
      if (answer === "SELL") return "border-red-500 hover:bg-red-500/20 text-red-400";
      return "border-yellow-500 hover:bg-yellow-500/20 text-yellow-400";
    }
    
    const isCorrect = answer === currentQuiz.correctAnswer;
    const isSelected = answer === selectedAnswer;
    
    if (isCorrect) return "border-green-500 bg-green-500/30 text-green-400";
    if (isSelected && !isCorrect) return "border-red-500 bg-red-500/30 text-red-400";
    return "border-zinc-600 text-zinc-500 opacity-50";
  };

  return (
    <div className="space-y-4 pb-20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-purple-400" />
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">Chart Quiz</h2>
            <p className="text-xs sm:text-sm text-zinc-400">Test your trading skills</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-green-400 border-green-500">
            Score: {score.correct}/{score.total}
          </Badge>
          <Button variant="outline" size="sm" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
        </div>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-zinc-400">Progress</span>
          <span className="text-zinc-300">{Math.round(progress)}% Complete</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Quiz Card */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Badge className={getDifficultyColor(currentQuiz.difficulty)}>
              {currentQuiz.difficulty}
            </Badge>
            <span className="text-zinc-400 text-sm">
              Question {currentIndex + 1}/{CHART_QUIZZES.length}
            </span>
          </div>
          <CardTitle className="text-white text-lg mt-2">{currentQuiz.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Chart Visual */}
          <ChartVisual pattern={currentQuiz.chartPattern} indicators={currentQuiz.indicators} />

          {/* Scenario Description */}
          <div className="bg-zinc-800 p-3 rounded-lg">
            <p className="text-zinc-300 text-sm">{currentQuiz.description}</p>
          </div>

          {/* Question */}
          <div className="text-center">
            <p className="text-white font-semibold flex items-center justify-center gap-2">
              <HelpCircle className="w-5 h-5 text-blue-400" />
              What would you do?
            </p>
          </div>

          {/* Answer Buttons */}
          <div className="grid grid-cols-3 gap-3">
            <Button
              variant="outline"
              className={`h-14 text-lg font-bold ${getButtonStyle("BUY")}`}
              onClick={() => handleAnswer("BUY")}
              disabled={showResult}
            >
              <TrendingUp className="w-5 h-5 mr-2" />
              BUY
            </Button>
            <Button
              variant="outline"
              className={`h-14 text-lg font-bold ${getButtonStyle("HOLD")}`}
              onClick={() => handleAnswer("HOLD")}
              disabled={showResult}
            >
              HOLD
            </Button>
            <Button
              variant="outline"
              className={`h-14 text-lg font-bold ${getButtonStyle("SELL")}`}
              onClick={() => handleAnswer("SELL")}
              disabled={showResult}
            >
              <TrendingDown className="w-5 h-5 mr-2" />
              SELL
            </Button>
          </div>

          {/* Result */}
          {showResult && (
            <div className={`p-4 rounded-lg ${
              selectedAnswer === currentQuiz.correctAnswer 
                ? "bg-green-500/20 border border-green-500" 
                : "bg-red-500/20 border border-red-500"
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {selectedAnswer === currentQuiz.correctAnswer ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-400" />
                    <span className="font-bold text-green-400">Correct!</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-6 h-6 text-red-400" />
                    <span className="font-bold text-red-400">
                      Incorrect - Answer was {currentQuiz.correctAnswer}
                    </span>
                  </>
                )}
              </div>
              <p className="text-sm text-zinc-300">{currentQuiz.explanation}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between gap-2">
        <Button 
          variant="outline" 
          onClick={handlePrev}
          className="flex-shrink-0 px-3 py-2 min-w-[100px]"
          size="lg"
        >
          <ChevronLeft className="w-5 h-5 mr-1" /> Prev
        </Button>
        
        <span className="text-zinc-400 text-sm font-medium">
          {currentIndex + 1}/{CHART_QUIZZES.length}
        </span>
        
        <Button 
          variant="outline" 
          onClick={handleNext}
          className="flex-shrink-0 px-3 py-2 min-w-[100px]"
          size="lg"
        >
          Next <ChevronRight className="w-5 h-5 ml-1" />
        </Button>
      </div>

      {/* Achievement */}
      {score.total >= 5 && score.correct / score.total >= 0.8 && (
        <Card className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border-yellow-500">
          <CardContent className="p-4 flex items-center gap-4">
            <Award className="w-10 h-10 text-yellow-400" />
            <div>
              <h4 className="font-bold text-yellow-400">Great Performance!</h4>
              <p className="text-sm text-zinc-300">
                You got {Math.round((score.correct / score.total) * 100)}% correct!
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
